import React from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
  FaInstagram,
} from "react-icons/fa6";

/**
 * ShareModal
 * Reusable share sheet for a community post. Extracted from Community.js so the
 * exact same share UI/UX can be used from the standalone PostDetailModal route
 * (e.g. /community/:id) where there is no feed-level share handler.
 *
 * @param {boolean} show     Whether the modal is visible
 * @param {object}  post     The post being shared (needs `id`, `author`, `content`)
 * @param {Function} onClose Called when the modal should close
 */
const ShareModal = ({ show, post, onClose }) => {
  if (!show || !post) return null;

  const shareToSocialMedia = (platform) => {
    // Feed posts expose `author`; the post-detail object uses `user_name`.
    const authorName = post.author || post.user_name || "someone";
    const shareText = `Check out this post by ${authorName}: ${
      post.content?.substring(0, 100) || ""
    }...`;
    // Build a canonical link to this post regardless of where the modal is open.
    const shareUrl = `${window.location.origin}/community/${post.id}`;
    let url = "";

    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl,
        )}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + shareUrl,
        )}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl,
        )}`;
        break;
      case "instagram":
        // Instagram doesn't have a web share API, so we copy the text + link
        // and try to open Instagram (mobile will open app, desktop opens web)
        const instagramText = `${shareText}\n\n${shareUrl}`;
        navigator.clipboard
          .writeText(instagramText)
          .then(async () => {
            onClose();
            await Swal.fire({
              icon: "success",
              title: "Content Copied!",
              text:
                "Post content and link copied to clipboard. You can now paste it on Instagram!",
              timer: 3000,
              showConfirmButton: false,
              customClass: { popup: "success-popup-modal" },
            });
            // After Swal closes, open Instagram
            window.open("https://www.instagram.com/", "_blank");
          })
          .catch(() => {
            toast.error("Failed to copy content");
          });
        return;
      case "copy":
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            toast.success("Link copied to clipboard!");
          })
          .catch(() => {
            toast.error("Failed to copy link");
          });
        onClose();
        return;
      default:
        return;
    }

    // Open social sharing URL in new window
    const popup = window.open(
      url,
      "_blank",
      "width=600,height=400,scrollbars=yes,resizable=yes",
    );
    if (!popup) {
      toast.warning("Please allow popups for this site to share content");
    } else {
      // Intent URLs don't report back whether the user actually posted, so
      // we can only confirm the share window opened — not claim success.
      const platformName =
        platform.charAt(0).toUpperCase() + platform.slice(1);
      toast.info(`Opening ${platformName} to finish sharing…`);
    }

    onClose();
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 24px",
    cursor: "pointer",
    borderBottom: "1px solid var(--mmp-border)",
    transition: "background-color 0.2s ease",
  };

  const iconBoxStyle = {
    width: "22px",
    height: "22px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
  };

  const onRowEnter = (e) =>
    (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)");
  const onRowLeave = (e) =>
    (e.currentTarget.style.backgroundColor = "transparent");

  return (
    <div
      className="modal-backdrop-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 99998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        className="modal-content-wrapper"
        style={{
          backgroundColor: "var(--mmp-surface)",
          borderRadius: "12px",
          maxWidth: "400px",
          width: "90%",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
          zIndex: 99999,
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px",
            borderBottom: "1px solid var(--mmp-border)",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "var(--mmp-text-primary)",
              margin: 0,
              lineHeight: "24px",
            }}
          >
            Share Post
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              color: "var(--mmp-text-secondary)",
              fontSize: "24px",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "0" }}>
          {/* Twitter */}
          <div
            onClick={() => shareToSocialMedia("twitter")}
            style={rowStyle}
            onMouseEnter={onRowEnter}
            onMouseLeave={onRowLeave}
          >
            <div style={{ ...iconBoxStyle, backgroundColor: "#1da1f2" }}>
              <FaXTwitter size={15} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Share on Twitter
            </span>
          </div>

          {/* Facebook */}
          <div
            onClick={() => shareToSocialMedia("facebook")}
            style={rowStyle}
            onMouseEnter={onRowEnter}
            onMouseLeave={onRowLeave}
          >
            <div style={{ ...iconBoxStyle, backgroundColor: "#1877f2" }}>
              <FaFacebookF size={15} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Share on Facebook
            </span>
          </div>

          {/* WhatsApp */}
          <div
            onClick={() => shareToSocialMedia("whatsapp")}
            style={rowStyle}
            onMouseEnter={onRowEnter}
            onMouseLeave={onRowLeave}
          >
            <div style={{ ...iconBoxStyle, backgroundColor: "#25d366" }}>
              <FaWhatsapp size={15} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Share on WhatsApp
            </span>
          </div>

          {/* LinkedIn */}
          <div
            onClick={() => shareToSocialMedia("linkedin")}
            style={rowStyle}
            onMouseEnter={onRowEnter}
            onMouseLeave={onRowLeave}
          >
            <div style={{ ...iconBoxStyle, backgroundColor: "#0077b5" }}>
              <FaLinkedinIn size={15} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Share on LinkedIn
            </span>
          </div>

          {/* Instagram */}
          <div
            onClick={() => shareToSocialMedia("instagram")}
            style={rowStyle}
            onMouseEnter={onRowEnter}
            onMouseLeave={onRowLeave}
          >
            <div
              style={{
                ...iconBoxStyle,
                background:
                  "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
              }}
            >
              <FaInstagram size={15} />
            </div>
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Share on Instagram
            </span>
          </div>

          {/* Copy Link */}
          <div
            onClick={() => shareToSocialMedia("copy")}
            style={{
              ...rowStyle,
              borderBottom: "none",
              borderBottomLeftRadius: "12px",
              borderBottomRightRadius: "12px",
            }}
            onMouseEnter={onRowEnter}
            onMouseLeave={onRowLeave}
          >
            <Copy style={{ width: "20px", height: "20px", color: "var(--mmp-text-secondary)" }} />
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Copy Link
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
